import React, { useState } from "react";
import { Instagram, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { apiUrl, fetchWithTimeout } from "../lib/api";
import { useSiteData } from "../context/SiteDataContext";
import { getSection, renderMultilineText, sectionStyle } from "../lib/sectionStyles";

const ContactCTA = () => {
  const { writer, siteSections } = useSiteData();
  const section = getSection(siteSections, "home", "home.contact", {
    eyebrow: "Section 07 - Letters to the Editor",
    title: "PITCH\nME.",
    body: "Cover features, profiles, fashion, music, culture, panels. Briefs welcome.",
    ctaLabel: "Request Inquiry",
    content: {
      formStamp: "FOR COMMISSION",
      formHeading: "Inquiry form",
      nameLabel: "Name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailPlaceholder: "your@email.com",
      messageLabel: "The Brief",
      messagePlaceholder: "Tell me about the story, commission, interview, or collaboration.",
    },
  });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [submitState, setSubmitState] = useState("idle");

  if (!section) return null;

  const titleLines = String(section.title || "PITCH\nME.").split(/\n+/).filter(Boolean);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setSubmitState("error");
      toast.error("Fill all fields, please.");
      return;
    }

    setSending(true);
    setSubmitState("loading");

    try {
      const response = await fetchWithTimeout(apiUrl("/api/inquiries"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.detail || "Could not send your inquiry.");
      }

      setSubmitState("success");
      setForm({ name: "", email: "", message: "" });
      toast.success("Inquiry received - I'll be in touch.");
    } catch (error) {
      setSubmitState("error");
      toast.error(error.message || "Could not send your inquiry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20" style={sectionStyle(section)}>
      <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="col-span-12 md:col-span-6">
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)]">
            {section.eyebrow}
          </span>
          <h2 className="mt-3 font-masthead leading-[0.85] text-7xl md:text-9xl">
            {titleLines[0] || "PITCH"}
            <br />
            <span className="text-[var(--red)]">{titleLines[1] || "ME."}</span>
          </h2>
          <p className="mt-5 font-editorial italic text-xl text-[var(--ink-soft)] max-w-md">
            {renderMultilineText(section.body)}
          </p>

          <ul className="mt-8 space-y-3 text-base">
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center bg-[var(--ink)] text-[var(--paper)]">
                <Mail size={15} />
              </span>
              <a className="uline" href={`mailto:${writer.email}`}>
                {writer.email}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center bg-[var(--pink)] text-[var(--ink)]">
                <Instagram size={15} />
              </span>
              <a className="uline" target="_blank" rel="noopener noreferrer" href={writer.instagramUrl}>
                @{writer.instagram}
              </a>
            </li>
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          className="col-span-12 md:col-span-6 relative border-2 border-[var(--ink)] bg-[var(--pink-soft)] p-6 md:p-8 shadow-[6px_6px_0_var(--ink)]"
        >
          <span
            aria-hidden="true"
            className="absolute top-0 right-0 w-0 h-0"
            style={{
              borderTop: "32px solid var(--pink)",
              borderLeft: "32px solid transparent",
            }}
          />
          <span className="stamp absolute -top-4 right-4">
            {section.content?.formStamp || "FOR COMMISSION"}
          </span>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-5">
            {section.content?.formHeading || "Inquiry form"}
          </p>

          <label className="block mb-4">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
              {section.content?.nameLabel || "Name"}
            </span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full bg-transparent border-b-2 border-[var(--ink)] focus:border-[var(--red)] outline-none py-2 font-editorial text-lg transition-colors"
              placeholder={section.content?.namePlaceholder || "Your name"}
            />
          </label>
          <label className="block mb-4">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
              {section.content?.emailLabel || "Email"}
            </span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full bg-transparent border-b-2 border-[var(--ink)] focus:border-[var(--red)] outline-none py-2 font-editorial text-lg transition-colors"
              placeholder={section.content?.emailPlaceholder || "your@email.com"}
            />
          </label>
          <label className="block mb-5">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
              {section.content?.messageLabel || "The Brief"}
            </span>
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full bg-transparent border-b-2 border-[var(--ink)] focus:border-[var(--red)] outline-none py-2 font-editorial text-lg resize-none transition-colors"
              placeholder={
                section.content?.messagePlaceholder ||
                "Tell me about the story, commission, interview, or collaboration."
              }
            />
          </label>
          <button
            type="submit"
            disabled={sending}
            aria-busy={submitState === "loading"}
            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] px-5 py-3 font-masthead tracking-[0.22em] text-sm hover:bg-[var(--red)] transition-colors disabled:opacity-60"
          >
            {sending ? "SENDING..." : section.ctaLabel || "Request Inquiry"}
            <Send size={14} />
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactCTA;
